import { Box, Button, Card, CardContent, Chip, Divider, Grid, makeStyles, TextField, Typography, InputAdornment, MenuItem, Select, FormControl, InputLabel } from '@material-ui/core'
import SearchField from '../SearchField'
import React, { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { gregorianToJalali, toFarsiDigits } from '../../assets/utils'
import { toEnDigits } from '../../assets/reportFilters'
import { exportExcel, reportMetaNotes, fetchReportQr } from '../../assets/excelUtils'
import { userInfoState } from '../../recoil/atoms'
import LockIcon from '@material-ui/icons/Lock'
import ChatIcon from '@material-ui/icons/Chat'
import VisibilityIcon from '@material-ui/icons/Visibility'
import ContractMessagesDialog from '../contracts/ContractMessagesDialog'
import ContractOpensDialog from '../contracts/ContractOpensDialog'
import { trackContractOpen } from '../../api/contractOpens'
import { isAdminImpersonatingCached } from '../../api/impersonation'
import SearchIcon from '@material-ui/icons/Search'
import GroupedReportDialog from './GroupedReportDialog'
import InsuranceReportDialog from './InsuranceReportDialog'

const useStyles = makeStyles(theme => ({
  root: { background: '#f4f6fb', flexGrow: 1 },
  // کارت قرارداد
  card: {
    borderRadius: 14,
    border: '1px solid #e3e6f2',
    boxShadow: '0 2px 10px rgba(20,30,80,.06)',
    transition: 'box-shadow .2s ease, transform .15s ease',
    '&:hover': {
      boxShadow: '0 8px 22px rgba(20,30,80,.14)',
      transform: 'translateY(-2px)',
    },
  },
  cardHeader: {
    background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
    color: '#fff',
    padding: '12px 16px',
    borderTopLeftRadius: 14,
    borderTopRightRadius: 14,
  },
  orgName: {
    fontWeight: 800,
    fontSize: 15,
    lineHeight: 1.6,
    color: '#fff',
  },
  contractNo: {
    fontSize: 11.5,
    color: 'rgba(255,255,255,.85)',
    fontWeight: 700,
  },
  subject: {
    fontSize: 12.5,
    color: '#5c6370',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
  reportBtn: {
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: 700,
    padding: '4px 12px',
    textTransform: 'none',
    whiteSpace: 'nowrap',
  },
  freshness: {
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 999,
    padding: '3px 10px',
    whiteSpace: 'nowrap',
  },
}))

// فقط اسم را برگردان (اگر id بود، همان شناسه)
const renderConsultant = (consultant) => {
  if (!consultant) return '—'
  if (typeof consultant === 'number' || typeof consultant === 'string') {
    return `شناسه: ${toFarsiDigits(consultant)}`
  }
  const name =
    consultant.name ??
    consultant.full_name ??
    consultant?.user?.name ??
    consultant?.user?.username ??
    'نامشخص'
  return toFarsiDigits(name)
}

const renderSupervisor = (supervisor) => {
  if (!supervisor) return '—'
  if (typeof supervisor === 'number' || typeof supervisor === 'string') {
    return `شناسه: ${toFarsiDigits(supervisor)}`
  }
  const name =
    supervisor.SupervisorName ??
    supervisor.name ??
    supervisor.full_name ??
    supervisor?.user?.name ??
    supervisor?.user?.username ??
    'نامشخص'
  return toFarsiDigits(name)
}

/** رنگ «تازگی» به‌روزرسانی سازمان: سبز ≤۳۰ روز، نارنجی ≤۹۰ روز، قرمز قدیمی‌تر */
const freshnessOf = (lastUpload) => {
  if (!lastUpload) return { bg: '#eceff1', fg: '#607d8b', dot: '⚪', label: 'بدون به‌روزرسانی' }
  const days = Math.floor((Date.now() - new Date(lastUpload).getTime()) / 86400000)
  if (days <= 30) return { bg: '#e8f5e9', fg: '#2e7d32', dot: '🟢', label: gregorianToJalali(lastUpload) }
  if (days <= 90) return { bg: '#fff3e0', fg: '#e65100', dot: '🟠', label: gregorianToJalali(lastUpload) }
  return { bg: '#ffebee', fg: '#c62828', dot: '🔴', label: gregorianToJalali(lastUpload) }
}

const SORTS = {
  latest_upload: 'تازه‌ترین به‌روزرسانی سازمان',
  number: 'شماره قرارداد',
  date: 'تاریخ قرارداد',
}

const Contracts = props => {
  const classes = useStyles()
  const userInfo = useRecoilValue(userInfoState)
  const navigate = useNavigate()

  // جستجوی زنده + مرتب‌سازی + فیلتر سازمان
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('latest_upload')
  const [orgFilter, setOrgFilter] = useState('')
  const [reportOpen, setReportOpen] = useState(false)
  const [insuranceReportId, setInsuranceReportId] = useState(null)
  const [msgContract, setMsgContract] = useState(null)
  const [opensContract, setOpensContract] = useState(null)
  // «گزارش بازدید» فقط برای ادمینی که از پنلِ ادمین «ورود به عنوان» کرده است
  const canSeeOpens = isAdminImpersonatingCached()
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  // سازمانهای موجود در قراردادها
  const orgs = useMemo(
    () => [...new Set((props.contracts || []).map((c) => c.contractee).filter(Boolean))],
    [props.contracts]
  )

  const contracts = useMemo(() => {
    let list = [...(props.contracts || [])]
    if (orgFilter) {
      list = list.filter((row) => row.contractee === orgFilter)
    }
    const q = toEnDigits(search).trim()
    if (q) {
      list = list.filter((row) => {
        const values = [
          row.contractee, row.number, row.subject,
          renderConsultant(row.consultant), renderSupervisor(row.supervisor),
        ]
        return values.some(v => v !== undefined && v !== null && toEnDigits(String(v)).includes(q))
      })
    }
    if (sortBy === 'latest_upload') {
      list.sort((a, b) => new Date(b.contractee_last_upload || 0) - new Date(a.contractee_last_upload || 0))
    } else if (sortBy === 'number') {
      list.sort((a, b) => String(a.number || '').localeCompare(String(b.number || ''), 'fa'))
    } else if (sortBy === 'date') {
      list.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
    }
    return list
  }, [props.contracts, search, sortBy, orgFilter])

  // خروجی اکسل فهرست قراردادها
  const exportContractsExcel = async () => {
    const list = contracts
    const firstId = list[0] && list[0].id
    const qr = firstId ? await fetchReportQr(firstId) : null
    exportExcel({
      filename: 'contracts.xlsx',
      title: `فهرست قراردادها — ${userInfo && userInfo.name ? userInfo.name : ''}`,
      headers: ['ردیف', 'سازمان طرف قرارداد', 'آخرین به‌روزرسانی سازمان', 'شماره', 'تاریخ', 'موضوع', 'مشاور', 'ناظر'],
      rows: list.map((row, idx) => [
        idx + 1,
        row.contractee,
        row.contractee_last_upload ? gregorianToJalali(row.contractee_last_upload) : '',
        row.number,
        gregorianToJalali(row.date),
        row.subject || 'نامشخص',
        (typeof row.consultant === 'object' && row.consultant && (row.consultant.name || row.consultant.full_name || 'شناسه ' + (row.consultant.id || ''))) || (row.consultant ? 'شناسه ' + row.consultant : '—'),
        (typeof row.supervisor === 'object' && row.supervisor && (row.supervisor.SupervisorName || row.supervisor.name || row.supervisor.full_name || 'شناسه ' + (row.supervisor.id || ''))) || (row.supervisor ? 'شناسه ' + row.supervisor : '—'),
      ]),
      notes: reportMetaNotes(),
      qrDataUri: qr,
    })
  }

  // دکمههای گزارش هر قرارداد — همان منطق قبلی (قفل/غیرفعال)
  const REPORTS = (row) => [
    { label: 'خلاصه', color: '#283593', bg: '#e8eaf6', onClick: () => props.setOpenSummary(row.id), disabled: false, lock: !userInfo.is_subscribed },
    { label: 'ضمانت‌نامه', color: '#00695c', bg: '#e0f2f1', onClick: () => props.setOpenGuarantees(row.id), disabled: !row.guarantee, lock: !userInfo.is_subscribed },
    { label: 'سپرده', color: '#ad1457', bg: '#fce4ec', onClick: () => props.setOpenDeposits(row.id), disabled: !row.deposit, lock: !userInfo.is_subscribed },
    { label: 'صورت‌وضعیت', color: '#e65100', bg: '#fff3e0', onClick: () => props.setOpenRequests(row.id), disabled: !row.request, lock: !userInfo.is_subscribed },
    { label: 'اسناد خزانه', color: '#2e7d32', bg: '#e8f5e9', onClick: () => props.setOpenTreasuryBills(row.id), disabled: !row.treasury_bill, lock: !userInfo.is_subscribed },
    { label: 'حواله‌ها', color: '#1565c0', bg: '#e3f2fd', onClick: () => props.setOpenTransfers(row.id), disabled: !row.transfer, lock: !userInfo.is_subscribed },
    { label: 'اسناد', color: '#4527a0', bg: '#ede7f6', onClick: () => props.setOpenDocuments(row.id), disabled: !row.document, lock: !userInfo.is_subscribed },
    { label: 'اعلامیه‌ها', color: '#00838f', bg: '#e0f7fa', onClick: () => props.setOpenPrintDocuments(row.id), disabled: !row.print_document, lock: false },
    { label: 'محدودیت‌ها', color: '#c62828', bg: '#ffebee', onClick: () => props.setOpenRestrictions(row.id), disabled: !row.restriction, lock: !userInfo.is_subscribed },
    { label: 'بیمه‌های پرداختی', color: '#00695c', bg: '#e0f2f1', onClick: () => setInsuranceReportId(row.id), disabled: !(row.document || row.transfer), lock: !userInfo.is_subscribed },
    { label: 'پیش‌پرداخت', color: '#6a1b9a', bg: '#f3e5f5', onClick: () => { trackContractOpen(row.id); navigate(`/contracts/${row.id}/prepayment`) }, disabled: false, lock: !userInfo.is_subscribed },
  ]

  return (
    <Card elevation={0} className={classes.root} data-tour="contracts-list">
      <Box style={{ padding: '6px 4px 0' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 10, marginBottom: 12 }}>
          <Typography variant="h6" style={{ fontWeight: 800, color: '#1a237e' }}>قرارداد‌ها</Typography>
          {(props.contracts || []).length > 0 && (
            <Button variant='contained' color='secondary' size='small' onClick={exportContractsExcel} data-tour="contractor-export">
              دانلود اکسل
            </Button>
          )}
        </Box>

        {/* جستجوی زنده + مرتب‌سازی */}
        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 10, marginBottom: 14 }} data-tour="contractor-search">
          <SearchField
            size="small" variant="outlined"
            placeholder="🔍 جستجو در سازمان، شماره، موضوع ... (همان لحظه)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ minWidth: 280, background: '#fff', borderRadius: 8 }}
          />
          <FormControl size="small" variant="outlined" style={{ minWidth: 210 }}>
            <InputLabel>مرتب‌سازی</InputLabel>
            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="مرتب‌سازی">
              {Object.entries(SORTS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </Select>
          </FormControl>
          {orgs.length > 1 && (
            <FormControl size="small" variant="outlined" style={{ minWidth: 220 }}>
              <InputLabel>سازمان</InputLabel>
              <Select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)} label="سازمان">
                <MenuItem value="">همه سازمانها</MenuItem>
                {orgs.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {(props.contracts || []).length !== contracts.length && (
            <Typography style={{ fontSize: 11.5, color: '#7a80a3' }}>
              {toFarsiDigits(contracts.length)} از {toFarsiDigits((props.contracts || []).length)} قرارداد
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={contracts.length === 0 || !userInfo.is_subscribed}
            style={{ fontWeight: 700 }}
            onClick={() => setReportOpen(true)}
            data-tour="contractor-financial"
          >
            📗 گزارش مالی
          </Button>
        </Box>

        {/* کارت قراردادها */}
        <Grid container spacing={2}>
          {contracts.map((row, idx) => {
            const fresh = freshnessOf(row.contractee_last_upload)
            return (
              <Grid item xs={12} md={6} key={row.id}>
                <Card className={classes.card} elevation={0}>
                  <Box className={classes.cardHeader}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 6 }}>
                      <Typography className={classes.orgName}>🏛 {toFarsiDigits(row.contractee)}</Typography>
                      <Typography className={classes.contractNo} dir="ltr">
                        {toFarsiDigits(row.number)} • {gregorianToJalali(row.date)}
                      </Typography>
                    </Box>
                  </Box>
                  <CardContent style={{ padding: '12px 16px 14px' }}>
                    <Typography className={classes.subject} title={row.subject || ''}>
                      موضوع: {toFarsiDigits(row.subject || 'نامشخص')}
                    </Typography>

                    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 6, marginTop: 8 }}>
                      <span className={classes.freshness} style={{ background: fresh.bg, color: fresh.fg }}>
                        {fresh.dot} به‌روزرسانی سازمان: {fresh.label}
                      </span>
                      <Chip size="small" label={`مشاور: ${renderConsultant(row.consultant)}`}
                        style={{ background: '#f4f6fb', color: '#3949ab', fontSize: 11, fontWeight: 700 }} />
                      <Chip size="small" label={`ناظر: ${renderSupervisor(row.supervisor)}`}
                        style={{ background: '#f4f6fb', color: '#6a1b9a', fontSize: 11, fontWeight: 700 }} />
                    </Box>

                    <Divider style={{ margin: '12px 0 10px', borderColor: '#eef0f8' }} />

                    {/* دکمههای گزارش */}
                    <Box display="flex" flexWrap="wrap" style={{ gap: 6 }} data-tour="contractor-reports">
                      {REPORTS(row).map((b) => (
                        <Button
                          key={b.label}
                          size="small"
                          variant="contained"
                          className={classes.reportBtn}
                          disabled={b.disabled}
                          style={{ color: b.disabled ? '#9aa1b9' : b.color, background: b.disabled ? '#f1f2f8' : b.bg }}
                          onClick={b.onClick}
                        >
                          {b.lock && <LockIcon style={{ fontSize: 13, marginLeft: 4 }} />}
                          {b.label}
                        </Button>
                      ))}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ChatIcon style={{ fontSize: 15 }} />}
                        className={classes.reportBtn}
                        onClick={() => { trackContractOpen(row.id); setMsgContract(row) }}
                        style={{ color: '#0b7285', borderColor: '#0b7285', fontSize: 11.5, fontWeight: 700 }}
                      >
                        گزارش پیام‌ها
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        className={classes.reportBtn}
                        onClick={() => { trackContractOpen(row.id); navigate(`/contracts/${row.id}/delay`) }}
                        style={{ color: '#4527a0', borderColor: '#4527a0', fontSize: 11.5, fontWeight: 700 }}
                      >
                        توقف و تأخیر
                      </Button>
                      {canSeeOpens && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon style={{ fontSize: 15 }} />}
                          className={classes.reportBtn}
                          onClick={() => { trackContractOpen(row.id); setOpensContract(row) }}
                          style={{ color: '#1a237e', borderColor: '#1a237e', fontSize: 11.5, fontWeight: 700 }}
                        >
                          گزارش بازدید
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
          {contracts.length === 0 && (
            <Grid item xs={12}>
              <Box style={{ textAlign: 'center', padding: 30, color: '#8a92b2' }}>
                {search ? 'قراردادی منطبق با جستجو یافت نشد' : 'قراردادی ثبت نشده است'}
              </Box>
            </Grid>
          )}
        </Grid>

        {/* گزارش پیام‌های قرارداد */}
        <ContractMessagesDialog
          open={!!msgContract}
          contractId={msgContract?.id}
          contractNumber={msgContract?.number}
          onClose={() => setMsgContract(null)}
        />

        {/* گزارشِ بازدیدِ قرارداد */}
        <ContractOpensDialog
          open={!!opensContract}
          contractId={opensContract?.id}
          contractNumber={opensContract?.number}
          onClose={() => setOpensContract(null)}
        />

        <GroupedReportDialog
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          contracts={contracts}
        />

        <InsuranceReportDialog
          open={!!insuranceReportId}
          onClose={() => setInsuranceReportId(null)}
          contractId={insuranceReportId}
        />
      </Box>
    </Card>
  )
}

export default Contracts
